import { AloftPrice, isDefined, isUndefined } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectBelowGradeFullBathCost,
  selectBelowGradeFullBathCosts,
} from './costSelectors/selectBelowGradeFullBathCost.ts';
import {
  selectBelowGradeHalfBathCost,
  selectBelowGradeHalfBathCosts,
} from './costSelectors/selectBelowGradeHalfBathCost.ts';
import {
  selectFinishedBasementAreaCost,
  selectFinishedBasementAreaCosts,
} from './costSelectors/selectFinishedBasementAreaCost.ts';
import { selectFullBathCost, selectFullBathCosts } from './costSelectors/selectFullBathCost.ts';
import { selectHalfBathCost, selectHalfBathCosts } from './costSelectors/selectHalfBathCost.ts';
import {
  selectMainStructureCost,
  selectMainStructureCosts,
} from './costSelectors/selectMainStructureCost.ts';
import {
  selectTotalBasementAreaCost,
  selectTotalBasementAreaCosts,
} from './costSelectors/selectTotalBasementAreaCost.ts';
import { selectCompSequence } from './orderSelectors.ts';

const calculateDwellingCostNew = (
  mainStructureCost: AloftPrice | undefined,
  basementFinished: AloftPrice | undefined,
  basementTotal: AloftPrice | undefined,
  fullBaths: AloftPrice | undefined,
  halfBaths: AloftPrice | undefined,
  belowGradeFullBaths: AloftPrice | undefined,
  belowGradeHalfBaths: AloftPrice | undefined,
) => {
  if (isUndefined(mainStructureCost)) {
    return undefined;
  }

  return [
    mainStructureCost,
    basementFinished,
    basementTotal,
    fullBaths,
    halfBaths,
    belowGradeFullBaths,
    belowGradeHalfBaths,
  ].reduce((acc: AloftPrice, curr) => acc.add(curr), AloftPrice.zero);
};

export const selectDwellingCostNew = createSelector(
  [
    selectMainStructureCost,
    selectFinishedBasementAreaCost,
    selectTotalBasementAreaCost,
    selectFullBathCost,
    selectHalfBathCost,
    selectBelowGradeFullBathCost,
    selectBelowGradeHalfBathCost,
  ],
  calculateDwellingCostNew,
);

export const selectDwellingCostNews = createSelector(
  [
    selectMainStructureCosts,
    selectFinishedBasementAreaCosts,
    selectTotalBasementAreaCosts,
    selectFullBathCosts,
    selectHalfBathCosts,
    selectBelowGradeFullBathCosts,
    selectBelowGradeHalfBathCosts,
    selectCompSequence,
  ],
  (
    mainStructureCosts,
    basementFinisheds,
    basementTotals,
    fullBaths,
    halfBaths,
    belowGradeFullBaths,
    belowGradeHalfBaths,
    compSequence,
  ) => {
    const properties = ['subject', ...compSequence];

    return properties.reduce((acc: Record<string, AloftPrice>, propID) => {
      const cost = calculateDwellingCostNew(
        mainStructureCosts[propID],
        basementFinisheds[propID],
        basementTotals[propID],
        fullBaths[propID],
        halfBaths[propID],
        belowGradeFullBaths[propID],
        belowGradeHalfBaths[propID],
      );

      if (isDefined(cost)) acc[propID] = cost;

      return acc;
    }, {});
  },
);

export const selectDwellingCostNewsDisplay = createSelector(
  [selectDwellingCostNews],
  (costNews) => {
    return Object.fromEntries(
      Object.entries(costNews).map(([propID, cost]) => [propID, cost.toString(true)]),
    );
  },
);
